// WikidataEntityDialog.jsx
// Popover dialog for displaying Wikidata entity information

import React, {useEffect, useState} from 'react';
import {Avatar, Box, IconButton, Link as MuiLink, Stack, Tooltip, Typography} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import {getEntityKBUrl, isLinkedEntityRef} from '../../../utils/entityLinks';
import {fetchKbEntitySummary} from '../../../utils/api/entities';
import {fetchWikidataSummary} from '../../../utils/wikidataService';
import {normalizeConfidencePercent, isAdminUser} from '../../entityMentions/MentionHoverCard';
import {coalesceEntityType, getEntityTypeLabel, isPlaceholderEntityType} from '../../../utils/entityTypeMeta';
import {resolveEntityAvatarUrl} from '../../../utils/entityAvatarUrl';
import FloatingPanel from './FloatingPanel';

const DESCRIPTION_PREVIEW_LIMIT = 30;

const TYPE_CHIP_SX = {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 600,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    lineHeight: 1.4,
    alignSelf: 'flex-start',
};

const KB_TYPE_CHIP_SX = {
    ...TYPE_CHIP_SX,
    bgcolor: 'info.main',
};

const TYPE_CHIP_LABEL_SX = {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    opacity: 0.85,
    mr: 0.35,
};

const SCORE_CHIP_SX = {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 0.5,
    fontSize: 11,
    fontWeight: 600,
    bgcolor: 'rgba(0,0,0,0.06)',
    color: 'text.secondary',
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    lineHeight: 1.4,
    fontVariantNumeric: 'tabular-nums',
};

const SCORE_LABEL_SX = {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.3,
    color: 'text.disabled',
};

const SCORE_VALUE_SX = {
    fontSize: 11,
    fontWeight: 700,
    color: 'text.primary',
};

/** Compact labelled score chip (NER / NEL) matching MentionHoverCard. */
function ScoreChip({label, percent, title}) {
    return (
        <Box component="span" sx={SCORE_CHIP_SX} title={title}>
            <Box component="span" sx={SCORE_LABEL_SX}>{label}</Box>
            <Box component="span" sx={SCORE_VALUE_SX}>{percent}%</Box>
        </Box>
    );
}

/** Trim a description to a short preview (≈30 chars), hiding the rest behind an ellipsis. */
export function truncateDescription(description) {
    if (typeof description !== 'string') return '';
    const trimmed = description.trim();
    if (trimmed.length <= DESCRIPTION_PREVIEW_LIMIT) return trimmed;
    return `${trimmed.slice(0, DESCRIPTION_PREVIEW_LIMIT).trimEnd()}…`;
}

function WikidataEntityContent({
    title,
    description,
    nerEntityType,
    kbEntityType,
    mentionPct,
    linkPct,
    imageUrl,
    kbUrl,
    onClose,
    handleUnlink,
    view,
    position,
}) {
    const fullDescription = typeof description === 'string' ? description.trim() : '';
    const preview = truncateDescription(fullDescription);
    const isTruncated = fullDescription.length > preview.length;
    const nerTypeLabel = nerEntityType ? getEntityTypeLabel(nerEntityType) : null;
    const kbTypeLabel = kbEntityType ? getEntityTypeLabel(kbEntityType) : null;
    const showKbType = Boolean(kbTypeLabel) && kbTypeLabel !== nerTypeLabel;
    const hasChips = Boolean(nerTypeLabel || showKbType || kbTypeLabel) || mentionPct != null || linkPct != null;
    return (
        <Stack direction="row" spacing={1.5} sx={{py: 0.5, px: 0.5}}>
            <Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
                <MuiLink
                    href={kbUrl}
                    variant="body1"
                    fontWeight={600}
                    sx={{alignSelf: 'flex-start'}}
                    underline="hover"
                    onClick={onClose}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {title}
                </MuiLink>
                {hasChips && (
                    <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center'}}>
                        {nerTypeLabel && (
                            <Tooltip title={`NER: ${nerTypeLabel}`} placement="top">
                                <Box component="span" sx={TYPE_CHIP_SX}>
                                    <Box component="span" sx={TYPE_CHIP_LABEL_SX}>NER</Box>
                                    {nerEntityType}
                                </Box>
                            </Tooltip>
                        )}
                        {(showKbType || (!nerTypeLabel && kbTypeLabel)) && (
                            <Tooltip title={`KB: ${kbTypeLabel}`} placement="top">
                                <Box component="span" sx={KB_TYPE_CHIP_SX}>
                                    <Box component="span" sx={TYPE_CHIP_LABEL_SX}>KB</Box>
                                    {kbEntityType}
                                </Box>
                            </Tooltip>
                        )}
                        {mentionPct != null && (
                            <ScoreChip label="NER" percent={mentionPct} title="Mention-detection confidence (NER)" />
                        )}
                        {linkPct != null && (
                            <ScoreChip label="NEL" percent={linkPct} title="Entity-linking confidence (NEL)" />
                        )}
                    </Box>
                )}
                {preview && (
                    <Tooltip title={isTruncated ? fullDescription : ''} placement="bottom-start">
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}
                        >
                            {preview}
                        </Typography>
                    </Tooltip>
                )}
            </Stack>
            <Box sx={{width: 64, minWidth: 64}}>
                {imageUrl ? (
                    <Avatar
                        variant="rounded"
                        src={imageUrl}
                        alt={title}
                        sx={{width: 64, height: 64}}
                    />
                ) : (
                    <Avatar variant="rounded" sx={{width: 64, height: 64}}>
                        {String(title || '?').slice(0, 1).toUpperCase()}
                    </Avatar>
                )}
            </Box>
            <Tooltip title="Unlink">
                <span>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={handleUnlink}
                        disabled={!view || position === undefined}
                        sx={{alignSelf: 'flex-start'}}
                    >
                        <LinkOffIcon fontSize="small"/>
                    </IconButton>
                </span>
            </Tooltip>
        </Stack>
    );
}

/**
 * Hydrate linked-entity popover fields from the KB summary ({@code GET /api/entities/{id}/summary}).
 * For linked entities this always loads KB type, description, and avatar. Mark attrs are kept as
 * the NER layer; Wikidata description is a last-resort fallback when the KB has none.
 *
 * @param {string|null} id - Wikidata QID or custom D/LOC ref
 * @param {string|null} initialEntityType - entityType already on the mark, if any
 * @param {string|null} initialDescription - description already on the mark, if any
 * @param {string|null} [initialImageUrl] - image URL already on the mark, if any
 * @returns {{nerEntityType: string|null, kbEntityType: string|null, entityType: string|null, description: string|null, imageUrl: string|null, canonicalName: string|null}}
 */
export function useHydratedEntityDetails(id, initialEntityType, initialDescription, initialImageUrl = null) {
    const linked = Boolean(id && isLinkedEntityRef(id));
    const [kbDetails, setKbDetails] = useState(null);
    const [wikidataDescription, setWikidataDescription] = useState(null);

    useEffect(() => {
        setKbDetails(null);
        setWikidataDescription(null);
        if (!linked) return undefined;

        let cancelled = false;
        fetchKbEntitySummary(id)
            .then((summary) => {
                if (!cancelled) setKbDetails(summary);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [id, linked]);

    const needsWikidataDescription = linked
        && !initialDescription
        && !kbDetails?.description;

    useEffect(() => {
        if (!needsWikidataDescription) return undefined;

        let cancelled = false;
        fetchWikidataSummary(id)
            .then((result) => {
                if (cancelled || !result?.summary?.description) return;
                setWikidataDescription(result.summary.description);
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [id, needsWikidataDescription]);

    const nerEntityType = isPlaceholderEntityType(initialEntityType)
        ? null
        : (initialEntityType ? String(initialEntityType).trim() : null);
    const kbEntityType = kbDetails?.entityType && !isPlaceholderEntityType(kbDetails.entityType)
        ? String(kbDetails.entityType).trim()
        : null;

    return {
        nerEntityType,
        kbEntityType,
        entityType: coalesceEntityType(kbEntityType, nerEntityType),
        description: kbDetails?.description || initialDescription || wikidataDescription,
        imageUrl: resolveEntityAvatarUrl({
            wikidataId: id,
            imageUrl: kbDetails?.imageUrl || initialImageUrl,
        }),
        canonicalName: kbDetails?.canonicalName || null,
    };
}

/**
 * Wikidata Entity Dialog Component
 * Displays entity information in a popover that follows the entity mark when scrolled
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {HTMLElement} props.anchorEl - Anchor element for positioning
 * @param {string} props.id - Wikidata entity ID (e.g., "Q42")
 * @param {string} props.label - Entity label (fallback if fetch fails)
 * @param {string|null} props.url - Source Wikidata URL stored on mark attrs
 * @param {string|null} props.description - Entity description stored on mark attrs
 * @param {string|null} props.imageUrl - Entity image URL stored on mark attrs
 * @param {{left: number, top: number}|null} props.anchorPosition - Optional absolute screen anchor for caret-driven open
 * @param {Function} props.onClose - Callback when dialog closes
 * @param {Object} props.view - ProseMirror editor view (for Unlink)
 * @param {number} props.position - Document position where the entity was clicked (for Unlink)
 * @param {string|null} props.uid - Unique mark instance ID (not Wikidata Q-id)
 */
export function WikidataEntityCard({
                                           id,
                                           uid,
                                           label: initialLabel,
                                           url: sourceUrl,
                                           description: initialDescription,
                                           entityType,
                                           confidence,
                                           nerScore,
                                           linkingScore,
                                           imageUrl: initialImageUrl,
                                           onClose,
                                           view,
                                           position
                                       }) {
    const kbUrl = getEntityKBUrl(id);

    // Handle Unlink action - removes the wikidataEntity mark
    const handleUnlink = () => {
        if (!view || position === undefined) {
            return;
        }

        const {state, dispatch} = view;
        const markType = state.schema.marks.wikidataEntity;

        if (!markType) {
            console.error('[WikidataEntityDialog] wikidataEntity mark type not found in schema');
            return;
        }

        const docSize = state.doc.content.size;
        const safePos = Math.max(0, Math.min(position, docSize));

        const findEntityMarkAtPos = (pos) => {
            const $pos = state.doc.resolve(pos);
            const marksHere = $pos.marks();
            if (uid) {
                return marksHere.find(m => m.type === markType && m.attrs.uid === uid) || null;
            }
            return marksHere.find(m => m.type === markType) || null;
        };

        let mark = findEntityMarkAtPos(safePos);
        let anchorPos = safePos;

        if (!mark && safePos > 0) {
            mark = findEntityMarkAtPos(safePos - 1);
            anchorPos = safePos - 1;
        }

        if (!mark) {
            onClose();
            return;
        }

        const markUid = uid || mark.attrs.uid || null;
        const isTargetMark = (candidate) => {
            if (candidate.type !== markType) return false;
            if (markUid) return candidate.attrs.uid === markUid;
            return candidate.eq(mark);
        };

        // Find the contiguous text range around the clicked position for this mark instance.
        const segments = [];
        state.doc.descendants((node, pos) => {
            if (!node.isText) return;
            segments.push({
                from: pos,
                to: pos + node.nodeSize,
                hasMark: node.marks.some(isTargetMark),
            });
        });

        let segmentIndex = segments.findIndex(seg => seg.hasMark && anchorPos >= seg.from && anchorPos <= seg.to);
        if (segmentIndex === -1 && anchorPos > 0) {
            const fallbackPos = anchorPos - 1;
            segmentIndex = segments.findIndex(seg => seg.hasMark && fallbackPos >= seg.from && fallbackPos <= seg.to);
        }

        if (segmentIndex === -1) {
            onClose();
            return;
        }

        let from = segments[segmentIndex].from;
        let to = segments[segmentIndex].to;

        for (let i = segmentIndex - 1; i >= 0; i -= 1) {
            const seg = segments[i];
            if (!seg.hasMark || seg.to !== from) break;
            from = seg.from;
        }

        for (let i = segmentIndex + 1; i < segments.length; i += 1) {
            const seg = segments[i];
            if (!seg.hasMark || seg.from !== to) break;
            to = seg.to;
        }

        const tr = state.tr.removeMark(from, to, mark);
        dispatch(tr);

        onClose();
    };

    const {
        nerEntityType,
        kbEntityType,
        description: resolvedDescription,
        imageUrl: resolvedImageUrl,
        canonicalName: kbCanonicalName,
    } = useHydratedEntityDetails(
        isLinkedEntityRef(id) ? id : null,
        entityType || null,
        initialDescription || null,
        initialImageUrl || null,
    );
    const displayTitle = kbCanonicalName || initialLabel || id || 'Wikidata entity';
    const admin = isAdminUser();
    const mentionPct = admin ? normalizeConfidencePercent(nerScore != null ? nerScore : confidence) : null;
    const linkPct = admin ? normalizeConfidencePercent(linkingScore) : null;
    const content = (
        <WikidataEntityContent
            title={displayTitle}
            description={resolvedDescription}
            nerEntityType={nerEntityType}
            kbEntityType={kbEntityType}
            mentionPct={mentionPct}
            linkPct={linkPct}
            imageUrl={resolvedImageUrl}
            kbUrl={kbUrl}
            onClose={onClose}
            handleUnlink={handleUnlink}
            view={view}
            position={position}
        />
    );

    return content;
}

function WikidataEntityDialogComponent({open, anchorEl, anchorPosition, onClose, ...props}) {
    return (
        <FloatingPanel
            open={open}
            anchorEl={anchorEl}
            anchorPosition={anchorPosition}
            onClose={onClose}
            id="wikidata-entity-dialog-panel"
            width={360}
            sx={{
                p: 1.5,
            }}
        >
            <WikidataEntityCard {...props} onClose={onClose} />
        </FloatingPanel>
    );
}

const WikidataEntityDialog = React.memo(WikidataEntityDialogComponent);

export default WikidataEntityDialog;
